import { Checkbox, FormControlLabel, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Organization } from '@app/types/organization';
import { getMenuItems } from '@app/utils/components';
import { isManualWorkLocationInput } from '@app/utils/productOrganization';

/** 「他の勤務地情報を入力する」チェックが操作するフォーム項目 */
type WorkLocationFormValues = {
  access: string;
  workAddress1: string;
  workAddress2: string;
  workCity: string;
  workLocationOther: boolean;
  workPostalCode: string;
  workPrefecture: string;
};

interface WorkLocationOtherFieldProps<T extends FieldValues> {
  control: Control<T>;
  /** 勤務先のSelectを幅いっぱいにするかどうか。新規登録・編集画面はtrue、一括編集画面はfalse */
  fullWidth?: boolean;
  organization: Organization;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
}

/**
 * 勤務先の組織表示と「他の勤務地情報を入力する」チェックボックス。
 * NOTE:人材紹介企業は法人住所が勤務地ではなく勤務地情報が常に手入力のため、まとめて非表示にする
 */
export function WorkLocationOtherField<T extends FieldValues>({
  control,
  fullWidth = false,
  organization,
  setValue,
  watch,
}: WorkLocationOtherFieldProps<T>) {
  const theme = useTheme();
  if (isManualWorkLocationInput(organization)) {
    return null;
  }

  // NOTE:呼び出し元のフォーム型は異なるが、いずれもWorkLocationFormValuesの項目を持つ前提でキャストする
  const workLocationOtherName = 'workLocationOther' as Path<T>;
  const setField = <K extends keyof WorkLocationFormValues>(
    name: K,
    value: WorkLocationFormValues[K]
  ) => setValue(name as unknown as Path<T>, value as PathValue<T, Path<T>>);
  const isWorkLocationOther = !!watch(workLocationOtherName);
  return (
    <>
      <Select
        value={organization.name}
        disabled={isWorkLocationOther}
        fullWidth={fullWidth}
        sx={{
          bgcolor: isWorkLocationOther
            ? theme.palette.action.disabledBackground
            : theme.palette.common.white,
          ...(fullWidth ? {} : { width: '25rem' }),
        }}
        displayEmpty
      >
        {getMenuItems([organization.name])}
      </Select>
      <Controller
        name={workLocationOtherName}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            {...field}
            checked={!!field.value}
            onChange={(e, checked) => {
              if (!checked) {
                // 勤務先で選ばれている勤務地情報を設定
                setField('workPostalCode', organization.postalCode);
                setField('workPrefecture', organization.addressLine1);
                setField('workCity', organization.addressLine2);
                setField('workAddress1', organization.addressLine3);
                setField(
                  'workAddress2',
                  organization.customFields.addressLine4 ?? ''
                );
                setField('access', organization.customFields.access ?? '');
              } else {
                setField('workPostalCode', '');
                setField('workPrefecture', '');
                setField('workCity', '');
                setField('workAddress1', '');
                setField('workAddress2', '');
                setField('access', '');
              }
              field.onChange(e, checked);
            }}
            control={<Checkbox />}
            label="他の勤務地情報を入力する"
          />
        )}
      />
    </>
  );
}
